import { z } from 'zod';
import { DELIVERY_CHANNELS } from './delivery.ts';
import { LocaleCode } from './localization.ts';

/**
 * Personalization helpers of the composer (BB-54, CNT-003, CMP-010): the variables a message may use, with type,
 * example and the snippet to insert, and a preview of the exact payload one profile would get.
 */
export const VARIABLE_TYPES = ['string', 'number', 'boolean', 'time', 'array', 'object', 'unknown'] as const;
export type VariableType = (typeof VARIABLE_TYPES)[number];

export interface TemplateVariable {
  /** `user`, `event_properties`, `api_trigger_properties`, `campaign`, `context`, … */
  namespace: string;
  /** Path below the namespace (`first_name`, `custom.tier`, `payment_transaction.value`). */
  path: string;
  type: VariableType;
  example?: unknown;
  description?: string;
  /** `pii` values are masked in previews without `pii.view`; `secret` event properties are never offered. */
  piiLevel: 'none' | 'pii';
  /** What the personalization button inserts, with an empty default. */
  liquid: string;
  /** For the event the variable comes from (action-based triggers). */
  event?: string;
}

export const VariablesQuery = z.object({ campaignId: z.uuid().optional() });
export interface VariablesResponse {
  variables: TemplateVariable[];
  /** Namespaces the message may read (no trigger data in scheduled campaigns). */
  namespaces: string[];
}

export const PreviewProfile = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('profile'), profileId: z.uuid() }),
  z.strictObject({ kind: z.literal('external_id'), externalId: z.string().min(1).max(512) }),
  /** A random active profile; the same seed picks the same profile (repeatable previews). */
  z.strictObject({ kind: z.literal('random'), seed: z.string().max(64).default('preview') }),
]);
export type PreviewProfile = z.infer<typeof PreviewProfile>;

export const PreviewBody = z
  .strictObject({
    channel: z.enum(DELIVERY_CHANNELS),
    /** A campaign's draft (else published) variant content… */
    campaignId: z.uuid().optional(),
    variantApiId: z.string().max(64).optional(),
    /** …or the channel content being edited. */
    content: z.record(z.string(), z.unknown()).optional(),
    profile: PreviewProfile,
    eventProperties: z.record(z.string(), z.unknown()).optional(),
    apiTriggerProperties: z.record(z.string(), z.unknown()).optional(),
    context: z.record(z.string(), z.unknown()).optional(),
    /** Preview another locale than the profile's (multi-language preview). */
    locale: LocaleCode.optional(),
    /** Attribute values for this render only (BB-59): `first_name`, a custom key or `custom.<key>`. */
    attributeOverrides: z.record(z.string().min(1).max(128), z.unknown()).optional(),
  })
  .refine((b) => b.campaignId || b.content, { message: 'send campaignId or content', path: ['content'] });
export type PreviewBody = z.infer<typeof PreviewBody>;

export interface PreviewResponse {
  channel: string;
  profileId: string;
  locale: string;
  /** The exact payload: FCM message, APNs headers and body, the SDK card, … */
  payload: unknown;
  /** Variables the profile has no value for: without a default they render empty. */
  unresolved: string[];
  usedDefaults: string[];
  aborted?: { reason: string; detail?: string };
  warnings: Array<{ code: 'payload_too_large' | 'insecure_link' | 'unresolved_variables' | 'masked_personal_data'; detail?: string }>;
}

/** `POST …/push/preview`: one push content previewed on every platform it is written for (BB-73). */
export const PushPreviewBody = z.strictObject({
  content: z.record(z.string(), z.unknown()),
  profile: PreviewProfile,
  eventProperties: z.record(z.string(), z.unknown()).optional(),
  apiTriggerProperties: z.record(z.string(), z.unknown()).optional(),
  context: z.record(z.string(), z.unknown()).optional(),
  locale: LocaleCode.optional(),
});
export type PushPreviewBody = z.infer<typeof PushPreviewBody>;
export interface PushPreviewResponse {
  android?: PreviewResponse;
  ios?: PreviewResponse;
}
